/**
 * The app shell: the sign-in gate, the collapsible sidebar and the page on
 * screen. Tools live in `components/`; the navigation is data in
 * `navigation.ts`; every page frames itself with `layout/PageLayout`.
 *
 * A page stays mounted once visited and is hidden, not unmounted, when the
 * editor moves on. A Headlines run takes minutes; switching to the Tutorial
 * mid-run used to drop the job, its polling and its results with the
 * component.
 *
 * Framed by the Media Manager (`embed.ts`) the shell has no sidebar: the
 * host's sidebar lists the tools, and the page gets the whole frame.
 */

import { LoaderCircle, LogIn } from 'lucide-react'
import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react'

// The default page loads with the shell; every other page is its own chunk,
// fetched the first time an editor opens it (pages then stay mounted).
import ArticleGenerator from './components/ArticleGenerator'
import ErrorBoundary from './components/ErrorBoundary'
import SessionExpiredBanner from './components/layout/SessionExpiredBanner'
import Sidebar from './components/layout/Sidebar'
import StartScreen, { StartCard, StartProgress } from './components/StartScreen'
import Button from './components/ui/Button'
import ConfirmHost from './components/ui/ConfirmHost'
import Toaster from './components/ui/Toaster'
import { host } from './embed'
import { useAccess } from './hooks/useAccess'
import { useActiveView } from './hooks/useActiveView'
import { useAuth } from './hooks/useAuth'
import { useSidebarCollapsed } from './hooks/useSidebarCollapsed'
import { navItem, type View } from './navigation'

const BreakingNews = lazy(() => import('./components/BreakingNews'))
const CryptoHeadlinesGenerator = lazy(() => import('./components/CryptoHeadlinesGenerator'))
const FindSimilarTool = lazy(() => import('./components/FindSimilarTool'))
const FormaliaCheckTool = lazy(() => import('./components/FormaliaCheckTool'))
const HeadlinesGenerator = lazy(() => import('./components/HeadlinesGenerator'))
const InfographicTool = lazy(() => import('./components/InfographicTool'))
const MorningBriefing = lazy(() => import('./components/MorningBriefing'))
const Newsflash = lazy(() => import('./components/Newsflash'))
const RevenueGenerator = lazy(() => import('./components/RevenueGenerator'))
const SeoTool = lazy(() => import('./components/SeoTool'))
const SingleHeadlineTool = lazy(() => import('./components/SingleHeadlineTool'))
const Tutorial = lazy(() => import('./components/Tutorial'))

const PAGES: Record<Exclude<View, 'tutorial'>, ComponentType> = {
    'breaking-news': BreakingNews,
    'morning-briefing': MorningBriefing,
    newsflash: Newsflash,
    'article-generator': ArticleGenerator,
    'headlines-generator': HeadlinesGenerator,
    'crypto-headlines-generator': CryptoHeadlinesGenerator,
    'single-headline': SingleHeadlineTool,
    'revenue-generator': RevenueGenerator,
    'formalia-check': FormaliaCheckTool,
    'seo-tool': SeoTool,
    infographics: InfographicTool,
    'find-similar': FindSimilarTool,
}

export default function App() {
    const { isAuthenticated, loading, login, logout, hostSignIn } = useAuth()
    const access = useAccess()
    const [activeView, navigate] = useActiveView()
    const [collapsed, toggleCollapsed] = useSidebarCollapsed()

    // The host's sidebar marks the page, also when a Tutorial link or the back
    // button changed it in here.
    useEffect(() => {
        host?.report(activeView)
    }, [activeView])

    // The browser tab and the window list name the page on screen.
    useEffect(() => {
        document.title = `${navItem(activeView).label} – HKCM News`
    }, [activeView])

    // Every page opened so far. Grown during render (React's "adjust state when
    // a prop changes" pattern) so a back-button navigation counts too.
    const [visited, setVisited] = useState<ReadonlySet<View>>(() => new Set([activeView]))
    if (!visited.has(activeView)) {
        setVisited(new Set(visited).add(activeView))
    }

    if (loading) {
        return (
            <StartScreen>
                <StartProgress>Anmeldung wird geprüft …</StartProgress>
            </StartScreen>
        )
    }

    if (!isAuthenticated && hostSignIn) {
        return (
            <StartScreen>
                <StartCard className="text-center">
                    <p className="text-sm text-fg-secondary">
                        Die Anmeldung aus dem Media Manager ist nicht angekommen. Bitte noch einmal versuchen oder den
                        Media Manager neu laden.
                    </p>
                    <Button size="lg" icon={LogIn} onClick={login} className="mt-5 w-full">
                        Noch einmal versuchen
                    </Button>
                </StartCard>
            </StartScreen>
        )
    }

    if (!isAuthenticated) {
        return (
            <StartScreen>
                <StartCard className="text-center">
                    <p className="text-sm text-fg-secondary">Bitte mit dem Microsoft-Konto anmelden.</p>
                    <Button size="lg" icon={LogIn} onClick={login} className="mt-5 w-full">
                        Mit Microsoft anmelden
                    </Button>
                </StartCard>
            </StartScreen>
        )
    }

    if (access === 'checking') {
        return (
            <StartScreen>
                <StartProgress>Zugriff wird geprüft …</StartProgress>
            </StartScreen>
        )
    }

    if (access === 'denied') {
        return (
            <StartScreen>
                <StartCard className="text-center">
                    <h2 className="text-base font-semibold text-fg-strong">Kein Zugriff</h2>
                    <p className="mt-2 text-sm text-fg-secondary">
                        Dieses Microsoft-Konto ist für das News Tool nicht freigeschaltet. Die Freischaltung vergibt
                        das KI-Team.
                    </p>
                    {!hostSignIn && (
                        <Button size="lg" variant="secondary" icon={LogIn} onClick={logout} className="mt-5 w-full">
                            Mit anderem Konto anmelden
                        </Button>
                    )}
                </StartCard>
            </StartScreen>
        )
    }

    return (
        <div className="flex h-screen overflow-hidden bg-canvas font-sans text-fg">
            {!host && (
                <Sidebar
                    activeView={activeView}
                    onNavigate={navigate}
                    collapsed={collapsed}
                    onToggleCollapsed={toggleCollapsed}
                />
            )}
            <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <SessionExpiredBanner />
                <div className="relative min-h-0 flex-1">
                    {[...visited].map((view) => {
                        const Page = view === 'tutorial' ? null : PAGES[view]
                        return (
                            <div key={view} hidden={view !== activeView} className="absolute inset-0">
                                <ErrorBoundary page>
                                    <Suspense fallback={<PageLoading />}>
                                        {Page ? <Page /> : <Tutorial onOpenTool={navigate} />}
                                    </Suspense>
                                </ErrorBoundary>
                            </div>
                        )
                    })}
                </div>
            </main>
            <Toaster />
            <ConfirmHost />
        </div>
    )
}

function PageLoading() {
    return (
        <div className="flex h-full items-center justify-center bg-canvas" role="status">
            <LoaderCircle className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
            <span className="sr-only">Seite wird geladen …</span>
        </div>
    )
}
